// The note, read rather than written. What `note-markdown.ts` parsed, as
// elements.
//
// IT EMITS REACT ELEMENTS AND NEVER A STRING OF HTML, which is the whole of this
// feature's safety story. There is no `dangerouslySetInnerHTML` here and no
// sanitizer in the bundle, because React escapes text by construction: a note
// full of `<script>` draws the characters somebody typed. The one thing markdown
// can carry that text cannot is a URL, and the parser has already refused every
// scheme it would not vouch for - a link with `href: null` becomes an `<a>` with
// no `href`, so `[bad](javascript:alert(1))` is inert while the words are still
// on screen.
//
// IT DECIDES NOTHING. Every rule about what the vocabulary means is in
// `note-markdown.ts`; this file is the mapping from blocks to tags, and the one
// judgement it holds is `base`, below.

import { Fragment } from 'react';
import { parseNote } from './note-markdown.ts';
import type { Block, Inline, ListBlock } from './note-markdown.ts';

interface Props {
  body: string;
  /**
   * The heading level `#` becomes. DECIDED BY THE CALLER, not guessed here.
   *
   * A rendered note is drawn under this screen's own `<h1>Note</h1>`, and
   * sometimes under a second heading as well: an ITEM note draws the item's
   * title as an `<h2>`, and a note of its own with a title draws that title as
   * an `<h2>` in this view. An untitled note has neither. So `#` cannot be one
   * level in both cases without leaving a hole in the outline in one of them,
   * and the caller is the only thing that knows which case it is in. `##` is
   * always one below.
   */
  base: 2 | 3;
}

/**
 * A note in its Read view.
 *
 * `<article>` rather than a bare div: it is a self-contained piece of somebody's
 * writing sitting inside a screen, which is exactly what the element is for, and
 * a screen reader can then jump to it.
 */
export function NoteMarkdown({ body, base }: Props) {
  const blocks = parseNote(body);

  if (blocks.length === 0) {
    // Not an error and not a failure - an empty note is the ordinary state of a
    // note somebody has just made. The Write view has the placeholder that says
    // what to do about it, so this only has to say which of the two is true.
    return (
      <p className="md md-empty">
        Nothing written yet. Switch to <b>Write</b> to start.
      </p>
    );
  }

  return (
    <article className="md">
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} base={base} />
      ))}
    </article>
  );
}

function BlockView({ block, base }: { block: Block; base: 2 | 3 }) {
  switch (block.kind) {
    case 'heading': {
      const level = base + block.level - 1;
      const Tag = `h${Math.min(level, 6)}` as 'h2' | 'h3' | 'h4';
      return (
        <Tag>
          <Runs parts={block.parts} />
        </Tag>
      );
    }
    case 'para':
      return (
        <p>
          <Lines lines={block.lines} />
        </p>
      );
    case 'quote':
      return (
        <blockquote>
          <p>
            <Lines lines={block.lines} />
          </p>
        </blockquote>
      );
    case 'code':
      // `<pre>` keeps every space that was typed, which is the point of a fence.
      return (
        <pre>
          <code>{block.text}</code>
        </pre>
      );
    case 'rule':
      return <hr />;
    case 'list':
      return <ListView list={block} base={base} />;
  }
}

function ListView({ list, base }: { list: ListBlock; base: 2 | 3 }) {
  const items = list.items.map((item, index) => (
    <li key={index} className={item.task === null ? undefined : 'md-task'}>
      {item.task !== null && (
        // A REAL CHECKBOX, not a coloured glyph, so a screen reader is told
        // ticked or not rather than being read a character.
        //
        // AND IT IS READ-ONLY IN THIS SLICE, deliberately. Ticking here would
        // rewrite the note's own text - `- [ ]` to `- [x]` - which is an edit to
        // somebody's words made from a view they opened to read, and this
        // product treats undo on a destructive act as a correctness
        // requirement. The tick that counts is on the day screen; this draws
        // what the note says.
        <input type="checkbox" checked={item.task} readOnly disabled />
      )}
      <span>
        <Runs parts={item.parts} />
      </span>
      {item.lists.map((nested, at) => (
        <ListView key={at} list={nested} base={base} />
      ))}
    </li>
  ));

  // `start` so a list somebody began at 3 still reads 3, 4, 5 - the numbers are
  // theirs, not a rendering detail.
  return list.ordered ? <ol start={list.start}>{items}</ol> : <ul>{items}</ul>;
}

/** Several lines in one block. A single newline IS a line break - see the parser. */
function Lines({ lines }: { lines: Inline[][] }) {
  return (
    <>
      {lines.map((parts, index) => (
        <Fragment key={index}>
          {index > 0 && <br />}
          <Runs parts={parts} />
        </Fragment>
      ))}
    </>
  );
}

function Runs({ parts }: { parts: Inline[] }) {
  return (
    <>
      {parts.map((part, index) => (
        <Run key={index} part={part} />
      ))}
    </>
  );
}

function Run({ part }: { part: Inline }) {
  switch (part.kind) {
    case 'text':
      return part.text;
    case 'strong':
      return (
        <strong>
          <Runs parts={part.parts} />
        </strong>
      );
    case 'em':
      return (
        <em>
          <Runs parts={part.parts} />
        </em>
      );
    case 'strike':
      // `<s>`, not `<del>`: `<del>` says an editor removed this from a
      // document, and a struck line in a note says the person is done with it.
      return (
        <s>
          <Runs parts={part.parts} />
        </s>
      );
    case 'code':
      return <code>{part.text}</code>;
    case 'link':
      return (
        // A NULL href IS THE REFUSAL, and it is drawn rather than hidden: the
        // words stay exactly as typed and only the link does not happen.
        // `noreferrer noopener` on every one - a note can hold a link to
        // anywhere, and `target="_blank"` without it hands the opened page a
        // handle on this one.
        <a
          href={part.href ?? undefined}
          target={part.href === null ? undefined : '_blank'}
          rel="noreferrer noopener"
        >
          <Runs parts={part.parts} />
        </a>
      );
  }
}
